import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { snakecaseKeysForApi } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCompany } from './queryKeys';

type EmployeeOfCompany = {
  email: string;
  firstName: string;
  id: UUID;
  isActive: boolean;
  isSynced?: boolean;
  lastName: string;
  position: string;
};

export type EmployeesOfCompanyPayload = {
  companyId: UUID;
  includeEmail?: boolean;
  includeInfo?: boolean;
};

export type EmployeesOfCompanyResponse = {
  users: EmployeeOfCompany[];
  paginatedResponse: {
    items: EmployeeOfCompany[];
    count: number;
  };
};

/**
 * Fetches the list of employees for a given company from the API.
 * @param companyPayload - Payload containing the company ID and optional parameters.
 * @returns A promise resolving to the list of employees and pagination details.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_company_employees_company__id__employees_get
 * @deprecated This endpoint is actually deprecated. However, it is needed for the dirty workaround in DeliveryNoteActionObject.initUserWithReactQueryFn.
 */
export const fetchEmployeesOfCompany = async (
  companyPayload: EmployeesOfCompanyPayload,
) => {
  const { companyId, ...params } = companyPayload;

  const response = await vestigasApi
    .get(ENDPOINT.COMPANY.GET_EMPLOYEES(companyId), {
      searchParams: snakecaseKeysForApi(params),
    })
    .json<EmployeesOfCompanyResponse>();

  return response;
};

/**
 * Custom React Query hook to fetch employees of a company.
 * @param companyPayload - Payload containing the company ID and optional parameters.
 * @param options - Additional options for configuring the query.
 * @returns The result of the useQuery hook.
 */
export const useQueryEmployeesOfCompany = <
  TSelected = EmployeesOfCompanyResponse,
>(
  companyPayload: EmployeesOfCompanyPayload,
  options?: Omit<
    UseQueryOptions<EmployeesOfCompanyResponse, Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<EmployeesOfCompanyResponse, Error, TSelected>({
    enabled: Boolean(companyPayload.companyId),
    queryFn: async () => fetchEmployeesOfCompany(companyPayload),
    queryKey: queryKeysCompany.getEmployees(companyPayload),
    ...options,
  });
};
